import { getSignerConfig } from "@/pages/esign/esign.config.ts";
import { useEmployeeReferenceCustomFields } from "@/queries/use-custom-fields.query.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import {
  ApiEsignConditionEntity,
  ApiEsignMarkupRole,
  EsignConditionType,
} from "@/types/esign.types.ts";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EsignSignerInput {
  condition_type: EsignConditionType;
  condition_value: Array<number | string>;
  condition_entities: ApiEsignConditionEntity[];
}

export interface EsignSignerModalProps extends ModalProps {
  conditionType: EsignConditionType;
  role?: ApiEsignMarkupRole;
  onSubmit: (input: EsignSignerInput) => void;
}

export function EsignSignerModal({
  conditionType,
  role,
  onSubmit,
  ...props
}: EsignSignerModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const isEditingSameType = role?.condition_type === conditionType;

  const [valueId, setValueId] = useState<number | string | null>(
    isEditingSameType ? (role?.condition_value?.[0] ?? null) : null
  );
  const [valueEntity, setValueEntity] = useState<ApiEsignConditionEntity | null>(
    isEditingSameType ? (role?.condition_entities?.[0] ?? null) : null
  );

  const isEmployee = conditionType === EsignConditionType.employee;
  const isCustomField =
    conditionType === EsignConditionType.affected_employee_custom_field_employee_reference;

  const employee = useEmployeeSearchInline(isEmployee);
  const customFields = useEmployeeReferenceCustomFields(isCustomField);

  const options: SelectItem[] = useMemo(() => {
    if (isEmployee) return employee.options;
    if (isCustomField)
      return customFields.data.map((field) => ({ id: field.id, title: field.title }));
    return [];
  }, [isEmployee, isCustomField, employee.options, customFields.data]);

  const canApply = valueId != null;

  const handleApply = () => {
    if (valueId == null) return;
    onSubmit({
      condition_type: conditionType,
      condition_value: [valueId],
      condition_entities: valueEntity ? [valueEntity] : [],
    });
    close(id);
  };

  return (
    <Modal
      {...props}
      title={t(getSignerConfig(conditionType).labelKey)}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <Button size={"lg"} disabled={!canApply} onClick={handleApply}>
            {t("core:esign_signer_select")}
          </Button>
          <Button variant={"plain"} size={"lg"} onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"md"}>
        {isEmployee && (
          <Select
            label={t("core:esign_select_employee")}
            emptyTitle={t("core:esign_select_employee")}
            options={valueEntity ? [valueEntity] : undefined}
            searchable={true}
            showAvatar={true}
            showCaption={true}
            loadOptionsOnOpen={true}
            onSearchOptions={employee.search}
            searchOptions={options}
            value={valueId}
            onChange={(value, entity) => {
              setValueId(value ?? null);
              setValueEntity(entity ? { id: entity.id, title: entity.title } : null);
            }}
          />
        )}

        {isCustomField && (
          <Select
            label={t("core:esign_select_custom_field")}
            emptyTitle={t("core:esign_select_custom_field")}
            options={options}
            value={valueId}
            onChange={(value, entity) => {
              setValueId(value ?? null);
              setValueEntity(entity ? { id: entity.id, title: entity.title } : null);
            }}
          />
        )}
      </Stack>
    </Modal>
  );
}
